package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.ContentReportReason
import com.elan.edubridge.shared.domain.model.ContentReportRequest
import com.elan.edubridge.shared.domain.model.ContentReportTargetType
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.icon
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLSelectElement
import org.w3c.dom.HTMLTextAreaElement

/** 게시글·댓글·작성자 신고가 같은 서버 사유 집합을 쓰므로 모달도 한 곳에서 공유한다. */
object ContentReportModal {
    private val scope = MainScope()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    fun open(
        academyId: String,
        targetType: ContentReportTargetType,
        targetId: String,
        postId: String? = null,
    ) {
        val targetLabel = when (targetType) {
            ContentReportTargetType.POST -> "게시글"
            ContentReportTargetType.COMMENT -> "댓글"
            ContentReportTargetType.USER -> "작성자"
        }
        val backdrop = document.createElement("div") as HTMLElement
        backdrop.className = "eb-modal-backdrop"
        backdrop.innerHTML = """
            <div class="eb-modal" role="dialog" aria-modal="true" aria-labelledby="content-report-title">
                <header class="eb-modal__header">
                    <h2 id="content-report-title">${targetLabel.escapeHtml()} 신고</h2>
                    <button class="eb-icon-button" id="content-report-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
                </header>
                <form class="eb-modal__body" id="content-report-form">
                    <p class="eb-meta">신고 내용은 학원 운영진 검토에만 사용돼요.</p>
                    <div class="eb-field">
                        <label for="content-report-reason">신고 사유</label>
                        <select id="content-report-reason" required>
                            ${ContentReportReason.entries.joinToString("") { reason -> """<option value="${reason.name}">${reason.displayName.escapeHtml()}</option>""" }}
                        </select>
                    </div>
                    <div class="eb-field">
                        <label for="content-report-detail">상세 설명 <span class="eb-meta">(선택)</span></label>
                        <textarea id="content-report-detail" maxlength="${ContentReportRequest.DETAIL_MAX_LENGTH}" rows="4" placeholder="운영진 검토에 필요한 내용만 입력해 주세요."></textarea>
                    </div>
                    <p class="eb-form-error" id="content-report-error" role="alert" hidden></p>
                </form>
                <footer class="eb-modal__footer">
                    <button class="eb-button eb-button--ghost" id="content-report-cancel" type="button">취소</button>
                    <button class="eb-button eb-button--primary" id="content-report-submit" type="submit" form="content-report-form">신고 접수</button>
                </footer>
            </div>
        """.trimIndent()
        document.body?.appendChild(backdrop)

        var submitting = false
        fun close() {
            if (!submitting) backdrop.remove()
        }
        fun setSubmitting(value: Boolean) {
            submitting = value
            listOf("#content-report-close", "#content-report-cancel", "#content-report-submit").forEach { selector ->
                (backdrop.querySelector(selector) as? HTMLButtonElement)?.disabled = value
            }
        }
        fun showError(message: String) {
            (backdrop.querySelector("#content-report-error") as? HTMLElement)?.let { error ->
                error.textContent = message
                error.removeAttribute("hidden")
            }
        }

        (backdrop.querySelector("#content-report-close") as? HTMLElement)?.onclick = { close(); null }
        (backdrop.querySelector("#content-report-cancel") as? HTMLElement)?.onclick = { close(); null }
        backdrop.onclick = { event -> if (event.target == backdrop) close(); null }
        (backdrop.querySelector("#content-report-form") as? HTMLFormElement)?.onsubmit = { event ->
            event.preventDefault()
            if (!submitting) {
                val reasonName = (backdrop.querySelector("#content-report-reason") as? HTMLSelectElement)?.value
                val reason = ContentReportReason.entries.firstOrNull { it.name == reasonName }
                val detail = (backdrop.querySelector("#content-report-detail") as? HTMLTextAreaElement)?.value?.trim()
                when {
                    reason == null -> showError("신고 사유를 선택해 주세요.")
                    (detail?.length ?: 0) > ContentReportRequest.DETAIL_MAX_LENGTH ->
                        showError("상세 설명은 ${ContentReportRequest.DETAIL_MAX_LENGTH}자 이하로 입력해 주세요.")
                    else -> {
                        setSubmitting(true)
                        scope.launch {
                            val result = commandRepository.reportContent(
                                ContentReportRequest(
                                    academyId = academyId,
                                    targetType = targetType,
                                    targetId = targetId,
                                    postId = postId,
                                    reason = reason,
                                    detail = detail?.takeIf { it.isNotBlank() },
                                ),
                            )
                            if (result.isSuccess) {
                                backdrop.remove()
                                when {
                                    result.alreadyReported -> Toast.success("이미 접수된 신고예요.")
                                    result.reopened -> Toast.success("신고를 다시 접수했어요.")
                                    else -> Toast.success("신고를 접수했어요.")
                                }
                            } else {
                                showError(result.errorMessage ?: "신고를 접수하지 못했어요. 다시 시도해 주세요.")
                                setSubmitting(false)
                            }
                        }
                    }
                }
            }
            null
        }
        (backdrop.querySelector("#content-report-reason") as? HTMLSelectElement)?.focus()
    }
}
